import type * as THREE from 'three';
import { t } from '../i18n.ts';
import { h } from './dom.ts';

/** Renderers that draw on the CPU: the browser has no GPU for the page (hardware acceleration off or no driver). */
const SOFTWARE = /SwiftShader|Basic Render Driver|llvmpipe|softpipe|Software Rasterizer|Software Adapter/i;
const KEY = 'somnhold.softGl.dismissed';
/** Once dismissed, it stays away this long. */
const QUIET_MS = 7 * 24 * 3600_000;

/** The GPU string the browser reports (the same one client-perf sends). */
const gpuName = (r: THREE.WebGLRenderer): string => {
  const gl = r.getContext();
  const info = gl.getExtension('WEBGL_debug_renderer_info');
  return String(gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER));
};

/**
 * A player drawing the game without the GPU (client-perf 2026-11-04: «Microsoft Basic Render Driver», 17–19 fps):
 * say so once, and how to turn hardware acceleration on. Never in automated browsers (the tests run on SwiftShader).
 */
export const softGlNotice = (r: THREE.WebGLRenderer): void => {
  if (navigator.webdriver || document.querySelector('[data-test=soft-gl]')) return;
  if (!SOFTWARE.test(gpuName(r))) return;
  try {
    if (Date.now() - Number(localStorage.getItem(KEY) ?? 0) < QUIET_MS) return;
  } catch {
    /* no storage: show it */
  }
  const el = h(
    'div.update-notice.soft-gl',
    { role: 'status', 'data-test': 'soft-gl' },
    h('span', {}, t('gl.software')),
    h('button.link', {
      onclick: () => {
        try {
          localStorage.setItem(KEY, String(Date.now()));
        } catch {
          /* fine */
        }
        el.remove();
      },
    }, t('gl.software.ok')),
  );
  document.body.append(el);
};
